import { useMemo } from "react";
import type { StoredProject } from "../model/storage.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import type { ElementRef } from "../model/inp/properties.ts";
import type { ElementDefaults } from "../model/elementDefaults.ts";
import type { ExternalFileReference } from "../model/inp/externalFiles.ts";
import { readBackdropCalibration } from "../model/inp/backdrop.ts";
import { setPropertyValueForMany } from "../model/inp/properties.ts";
import { getTextTranslation } from "../i18n/textRegistry.ts";
import type { Translate } from "../i18n/hooks.ts";
import { AlertDialog } from "./AlertDialog.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { OptionsDialog } from "./OptionsDialog.tsx";
import { DefaultsDialog } from "./DefaultsDialog.tsx";
import { GroupEditDialog } from "./GroupEditDialog.tsx";
import { GisImportDialog } from "./GisImportDialog.tsx";
import { ProjectDataDialog, type DataTab } from "./ProjectDataDialog.tsx";
import { BackgroundImageDialog } from "./BackgroundImageDialog.tsx";
import { GeoreferenceDialog } from "./GeoreferenceDialog.tsx";
import { SimulationSummaryDialog } from "./SimulationSummaryDialog.tsx";
import { StatisticsReportDialog } from "./StatisticsReportDialog.tsx";
import { LongitudinalProfileDialog } from "./LongitudinalProfileDialog.tsx";
import { ExternalFilesDialog } from "./ExternalFilesDialog.tsx";
import type { useDialogToggles } from "./useDialogToggles.ts";
import type { useLongitudinalProfile } from "./useLongitudinalProfile.ts";
import type { useSimulationWorker } from "./useSimulationWorker.ts";
import type { useProjectDataActions } from "./useProjectDataActions.ts";
import type { PendingDelete } from "./useDeleteFlow.ts";

/** A stable empty `Map` for `ExternalFilesDialog.tsx`'s own `supplied` prop when `loaded.externalFiles` is `undefined` — avoids handing it a fresh `new Map()` (and so a new reference) on every render. */
const EMPTY_EXTERNAL_FILES: ReadonlyMap<string, string> = new Map();

/** `georeferenceWarnings`/`malformedLinkWarnings` are raw English strings registered via `i18n/textRegistry.ts` (`georeference.ts`/`parseInp.ts`'s own model-layer warnings) — translated here the same way `SimulationWarningsBanner.tsx` translates the engine's own warnings, falling back to the raw text for anything never routed through the registry. */
function translateWarnings(warnings: string[], t: Translate): string {
  return warnings
    .map((w) => {
      const translation = getTextTranslation(w);
      return translation ? t(translation.key, translation.params) : w;
    })
    .join(" ");
}

/**
 * Every alert/confirm/settings dialog App.tsx can pop up, flattened into one
 * component — split out of App.tsx (P1 deep-review: 1,342 lines / 105-file
 * fan-out, no god-module). Each dialog keeps its own existing open condition
 * and props verbatim; this is a pure JSX relocation, not a behavior change.
 */
export function AppDialogs({
  loaded,
  project,
  t,
  dialogs,
  pendingDelete,
  onClosePendingDelete,
  simulationWorker,
  storageError,
  onCloseStorageError,
  georeferenceWarnings,
  onCloseGeoreferenceWarnings,
  malformedLinkWarnings,
  onCloseMalformedLinkWarnings,
  externalFilesMissing,
  onSupplyExternalFile,
  projectDataActions,
  projectIsEmpty,
  longitudinalProfile,
  elementDefaults,
  onSaveElementDefaults,
  canGroupEdit,
  multiSelected,
  mutateRawUndoable,
  projectDataTab,
  setProjectDataTab,
}: {
  loaded: StoredProject;
  project: SwmmProject;
  t: Translate;
  dialogs: ReturnType<typeof useDialogToggles>;
  pendingDelete: PendingDelete | null;
  onClosePendingDelete: () => void;
  simulationWorker: ReturnType<typeof useSimulationWorker>;
  storageError: string | null;
  onCloseStorageError: () => void;
  georeferenceWarnings: string[] | null;
  onCloseGeoreferenceWarnings: () => void;
  malformedLinkWarnings: string[] | null;
  onCloseMalformedLinkWarnings: () => void;
  externalFilesMissing: ExternalFileReference[];
  onSupplyExternalFile: (basename: string, fileName: string, content: string) => void;
  projectDataActions: ReturnType<typeof useProjectDataActions>;
  projectIsEmpty: boolean;
  longitudinalProfile: ReturnType<typeof useLongitudinalProfile>;
  elementDefaults: ElementDefaults;
  onSaveElementDefaults: (form: ElementDefaults) => void;
  canGroupEdit: boolean;
  multiSelected: ElementRef[];
  mutateRawUndoable: (
    fn: (raw: ReadonlyMap<string, string[][]>) => ReadonlyMap<string, string[][]>
  ) => void;
  projectDataTab: DataTab | null;
  setProjectDataTab: (tab: DataTab | null) => void;
}) {
  // Only actually read once BackgroundImageDialog below is open, but keyed
  // on rawSections rather than recomputed inline so it doesn't re-parse on
  // every re-render while that dialog stays open (same reasoning as
  // App.tsx's own `project`/`backdropCalibration` memos).
  const backdropCalibration = useMemo(
    () => readBackdropCalibration(loaded.rawSections),
    [loaded.rawSections]
  );

  return (
    <>
      {pendingDelete?.kind === "blocked" && (
        <AlertDialog
          title={pendingDelete.title}
          message={pendingDelete.message}
          onClose={onClosePendingDelete}
        />
      )}
      {simulationWorker.simulationError && (
        <AlertDialog
          title={t("simulation.failedTitle")}
          message={t("simulation.failedMessage", { message: simulationWorker.simulationError })}
          onClose={() => simulationWorker.dismissError()}
        />
      )}
      {storageError && (
        <AlertDialog
          title={t("storage.errorTitle")}
          message={t("storage.errorMessage", { message: storageError })}
          onClose={onCloseStorageError}
        />
      )}
      {georeferenceWarnings && (
        <AlertDialog
          title={t("georeference.warningsTitle")}
          message={translateWarnings(georeferenceWarnings, t)}
          onClose={onCloseGeoreferenceWarnings}
        />
      )}
      {malformedLinkWarnings && (
        <AlertDialog
          title={t("malformedLinks.warningsTitle")}
          message={translateWarnings(malformedLinkWarnings, t)}
          onClose={onCloseMalformedLinkWarnings}
        />
      )}
      {pendingDelete?.kind === "confirm" && (
        <ConfirmDialog
          title={pendingDelete.title}
          message={pendingDelete.message}
          confirmLabel={t("delete.confirmButton")}
          onConfirm={pendingDelete.onConfirm}
          onClose={onClosePendingDelete}
        />
      )}
      {dialogs.open.simulationSummary && simulationWorker.results && (
        <SimulationSummaryDialog
          summary={simulationWorker.results.summary}
          flowUnits={project.flowUnits}
          onClose={() => dialogs.close("simulationSummary")}
        />
      )}
      {dialogs.open.statisticsReport && simulationWorker.results && (
        <StatisticsReportDialog
          project={project}
          results={simulationWorker.results}
          flowUnits={project.flowUnits}
          onClose={() => dialogs.close("statisticsReport")}
        />
      )}
      {dialogs.open.externalFiles && (
        <ExternalFilesDialog
          missing={externalFilesMissing}
          supplied={loaded.externalFiles ?? EMPTY_EXTERNAL_FILES}
          onSupply={onSupplyExternalFile}
          onRun={() => {
            dialogs.close("externalFiles");
            simulationWorker.start({
              rawSections: loaded.rawSections,
              externalFiles: loaded.externalFiles,
            });
          }}
          onClose={() => dialogs.close("externalFiles")}
        />
      )}
      {dialogs.open.gisImport && (
        <GisImportDialog
          rawSections={loaded.rawSections}
          currentCrs={loaded.crs}
          projectIsEmpty={projectIsEmpty}
          onImport={projectDataActions.importLayers}
          onClose={() => dialogs.close("gisImport")}
        />
      )}
      {dialogs.open.georeference && (
        <GeoreferenceDialog
          project={project}
          onApply={projectDataActions.applyGeoreference}
          onClose={() => dialogs.close("georeference")}
        />
      )}
      {longitudinalProfile.open &&
        longitudinalProfile.startNode !== null &&
        longitudinalProfile.endNode !== null && (
          <LongitudinalProfileDialog
            project={project}
            results={simulationWorker.results}
            startNode={longitudinalProfile.startNode}
            endNode={longitudinalProfile.endNode}
            onStartNodeChange={longitudinalProfile.setStartNode}
            onEndNodeChange={longitudinalProfile.setEndNode}
            onPickStart={() => longitudinalProfile.requestPick("start")}
            onPickEnd={() => longitudinalProfile.requestPick("end")}
            onClose={longitudinalProfile.close}
          />
        )}
      {dialogs.open.options && (
        <OptionsDialog
          rawSections={loaded.rawSections}
          onSave={(form) => {
            projectDataActions.saveOptions(form);
            dialogs.close("options");
          }}
          onClose={() => dialogs.close("options")}
        />
      )}
      {dialogs.open.elementDefaults && (
        <DefaultsDialog
          defaults={elementDefaults}
          onSave={(form) => {
            onSaveElementDefaults(form);
            dialogs.close("elementDefaults");
          }}
          onClose={() => dialogs.close("elementDefaults")}
        />
      )}
      {dialogs.open.groupEdit && canGroupEdit && (
        <GroupEditDialog
          project={project}
          selection={multiSelected}
          onApply={(section, columnIndex, value) => {
            mutateRawUndoable((raw) =>
              setPropertyValueForMany(
                raw,
                section,
                multiSelected.map((r) => r.name),
                columnIndex,
                value
              )
            );
            dialogs.close("groupEdit");
          }}
          onClose={() => dialogs.close("groupEdit")}
        />
      )}
      {projectDataTab && (
        <ProjectDataDialog
          rawSections={loaded.rawSections}
          onlyTab={projectDataTab}
          onSave={(raw) => {
            projectDataActions.saveProjectData(raw);
            setProjectDataTab(null);
          }}
          onClose={() => setProjectDataTab(null)}
        />
      )}
      {dialogs.open.backgroundImage && (
        <BackgroundImageDialog
          project={project}
          currentBlob={loaded.backgroundImageBlob}
          currentCalibration={backdropCalibration}
          onApply={(blob, calibration) => {
            projectDataActions.saveBackgroundImage(blob, calibration);
            dialogs.close("backgroundImage");
          }}
          onClose={() => dialogs.close("backgroundImage")}
        />
      )}
    </>
  );
}
